/**
 * Questions-to-Go Component
 * Shows all questions with status indicators (pending vs answered)
 * 
 * Single filter control: [Live | All] segmented button
 * - Transcript tab → defaults to "Live" (live questions only)
 * - Notes/Summary tab → auto-switches to "All" (all questions)
 * - User can override manually at any time
 * 
 * Questions are always sorted with unanswered (pending) at the top.
 */

import React, { useMemo } from 'react';
import Text from '@amzn/meridian/text';
import Badge from '@amzn/meridian/badge';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import sparklesLargeTokens from '@amzn/meridian-tokens/base/icon/sparkles-large';
import { Flipper, Flipped } from 'react-flip-toolkit';
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { updateSessionFields } from '../../../store/sessionSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { FIELD_BG_ANSWERED, FIELD_BG_NOT_SURE, FIELD_BG_UNANSWERED, FONT_SIZE_BODY, FONT_SIZE_XSMALL, FONT_SIZE_ICON, FONT_SIZE_EMPTY_STATE_ICON, LINE_HEIGHT_BODY, TEXT_COLOR_PRIMARY, TEXT_COLOR_SECONDARY, TEXT_COLOR_LINK, FIELD_ICON_ANSWERED, FIELD_ICON_NOT_SURE, TRANSITION_BG_COLOR } from '../cssConstants';
import { getFieldOrder, getFieldQuestion, isFieldNotApplicable, isFieldExtracting, isFieldPending, FieldValue } from '../../../utils/fieldUtils';
import { useFieldChangeHighlight, HIGHLIGHT_PULSE_CSS, getHighlightStyle } from '../../../utils/animationUtils';

export type CategoryFilter = 'live' | 'all';

interface QuestionsToGoProps {
  fieldNames: string[];
  extractedFields?: Partial<Record<string, FieldValue>>;
  sessionId?: string;
  /** Current category filter — controlled by parent */
  categoryFilter: CategoryFilter;
  /** Callback when user changes the category filter */
  onCategoryFilterChange: (filter: CategoryFilter) => void;
}

const QuestionsToGo: React.FC<QuestionsToGoProps> = ({
  fieldNames,
  extractedFields,
  sessionId,
  categoryFilter,
  onCategoryFilterChange,
}) => {
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const dispatch = useAppDispatch();

  // Derived (not a prop): a field is answered when it has a value that is not
  // pending. Memoized so the Set identity is stable across renders, which keeps
  // useFieldChangeHighlight from firing on unrelated re-renders.
  const answeredFields = useMemo(() => {
    const answered = new Set<string>();
    getFieldOrder(fieldsConfig).forEach((fieldName) => {
      const value = extractedFields?.[fieldName];
      if (value && !isFieldPending(value)) answered.add(fieldName);
    });
    return answered;
  }, [fieldsConfig, extractedFields]);

  const pendingCount = fieldNames.filter(field => !answeredFields.has(field)).length;
  const recentlyChanged = useFieldChangeHighlight(answeredFields);

  const handleMarkComplete = async (fieldName: string) => {
    if (!sessionId) return;
    try {
      await dispatch(updateSessionFields({
        sessionId,
        fields: { [fieldName]: '<NOT_APPLICABLE>' },
      })).unwrap();
      dispatch(showSuccess('Marked as not applicable'));
    } catch (error: any) {
      dispatch(showError(error || 'Failed to mark as complete'));
    }
  };

  // Sort: unanswered first, answered/extracting middle, N/A bottom
  const sortedFields = [...fieldNames].sort((a, b) => {
    const aAnswered = answeredFields.has(a);
    const bAnswered = answeredFields.has(b);
    const aNa = isFieldNotApplicable(extractedFields?.[a]);
    const bNa = isFieldNotApplicable(extractedFields?.[b]);

    const getOrder = (answered: boolean, na: boolean) => {
      if (!answered) return 0; // unanswered first
      if (answered && !na) return 1; // answered/extracting middle
      return 2; // N/A bottom
    };

    return getOrder(aAnswered, aNa) - getOrder(bAnswered, bNa);
  });

  const renderFieldItem = (fieldName: string) => {
    const question = getFieldQuestion(fieldsConfig, fieldName);
    const isAnswered = answeredFields.has(fieldName);
    const isExtracting = isFieldExtracting(extractedFields?.[fieldName]);
    const isNa = isFieldNotApplicable(extractedFields?.[fieldName]);
    const isHighlighted = recentlyChanged.has(fieldName);

    // Yellow background for N/A, green for answered/extracting, grey for unanswered
    const bgColor = isNa ? FIELD_BG_NOT_SURE : (isAnswered || isExtracting) ? FIELD_BG_ANSWERED : FIELD_BG_UNANSWERED;

    return (
      <Flipped key={fieldName} flipId={`question-${fieldName}`}>
        <div
          style={{
            padding: '12px',
            backgroundColor: bgColor,
            fontSize: FONT_SIZE_BODY,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            borderBottom: '1px solid #e0e0e0',
            transition: TRANSITION_BG_COLOR,
            ...getHighlightStyle(isHighlighted),
          }}
        >
          <span style={{
            fontSize: FONT_SIZE_ICON,
            lineHeight: LINE_HEIGHT_BODY,
            color: isNa ? FIELD_ICON_NOT_SURE : ((isAnswered || isExtracting) ? FIELD_ICON_ANSWERED : TEXT_COLOR_SECONDARY),
            flexShrink: 0,
          }}>
          </span>
          <div style={{
            flex: 1,
            color: TEXT_COLOR_PRIMARY,
          }}>
            <span>{question}</span>
            {isExtracting && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                <Loader size="small" />
                <span style={{ fontSize: '12px', color: TEXT_COLOR_SECONDARY, fontStyle: 'italic' }}>
                  Information captured, extracting...
                </span>
              </div>
            )}
          </div>
          {!isAnswered && !isExtracting && sessionId && (
            <Button type="link" size="small" onClick={() => handleMarkComplete(fieldName)}>
              Skip
            </Button>
          )}
        </div>
      </Flipped>
    );
  };

  // Segmented button style helper
  const getSegmentStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '4px 8px',
    border: 'none',
    borderRadius: '3px',
    fontSize: FONT_SIZE_XSMALL,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#fff' : TEXT_COLOR_SECONDARY,
    backgroundColor: isActive ? TEXT_COLOR_LINK : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header: "Question-to-Go (N)" on left, [Live|All] on right */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text type="b400" tag="span"><strong>Question-to-Go</strong></Text>
          <Badge value={pendingCount} />
        </div>

        {/* Segmented control */}
        <div style={{
          display: 'flex',
          backgroundColor: '#f0f2f4',
          borderRadius: '4px',
          padding: '2px',
        }}>
          <button
            onClick={() => onCategoryFilterChange('live')}
            style={getSegmentStyle(categoryFilter === 'live')}
          >
            Live
          </button>
          <button
            onClick={() => onCategoryFilterChange('all')}
            style={getSegmentStyle(categoryFilter === 'all')}
          >
            All
          </button>
        </div>
      </div>

      {/* Content */}
      {!fieldsConfig ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          flex: 1,
        }}>
          <div style={{ fontSize: FONT_SIZE_EMPTY_STATE_ICON, marginBottom: '12px' }}>
            <Icon tokens={sparklesLargeTokens} />
          </div>
          <Text type="h300" color="primary">No questions configured</Text>
        </div>
      ) : sortedFields.length === 0 && answeredFields.size > 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          flex: 1,
        }}>
          <div style={{ fontSize: FONT_SIZE_EMPTY_STATE_ICON, marginBottom: '12px' }}>
            <Icon tokens={sparklesLargeTokens} />
          </div>
          <Text type="h300" color="primary">All questions answered!</Text>
        </div>
      ) : (
        <div style={{
          flex: 1,
          overflowY: 'auto',
        }}>
          <style>{HIGHLIGHT_PULSE_CSS}</style>
          <Flipper flipKey={sortedFields.join(',')} spring="gentle">
            {sortedFields.map((fieldName) => renderFieldItem(fieldName))}
          </Flipper>
        </div>
      )}
    </div>
  );
};

export default QuestionsToGo;
